/**
 * ProviderDangerZoneSection — hard-delete UI for inactive providers.
 * Visible only when can_manage && status === inactive; type-to-confirm before DELETE.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, AlertMessage, ConfirmDialog } from "@/components/reusable";
import { deleteProvider } from "@/services/providersApi";
import { useTelemetry } from "@/hooks/useTelemetry";
import type { ProviderOut } from "@/types/providers";

interface ProviderDangerZoneSectionProps {
  provider: ProviderOut;
  isAdmin: boolean;
}

/** Irreversible cascade-delete affordance for inactive providers. */
export function ProviderDangerZoneSection({ provider, isAdmin }: ProviderDangerZoneSectionProps) {
  const { track, trackError } = useTelemetry();
  const navigate = useNavigate();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  if (!isAdmin || provider.provider_status !== "inactive") {
    return null;
  }

  async function handleHardDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteProvider(provider.provider_id);
      track("provider_hard_deleted", { providerId: provider.provider_id });
      navigate("/provider-management/providers");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to delete provider.";
      setDeleteError(msg);
      trackError(err instanceof Error ? err : new Error(msg), { context: "provider_hard_delete" });
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <section className="rounded-xl border border-red-200 bg-red-50/40 p-5">
        <h2 className="mb-1 text-sm font-semibold text-red-800">Danger zone</h2>
        <p className="mb-4 text-sm text-red-700/90">
          Permanently delete this provider and all related schema assignments, known mappings, user
          links, and ingestion records. This cannot be undone. The provider must stay inactive.
        </p>
        {deleteError && (
          <div className="mb-3">
            <AlertMessage variant="error" message={deleteError} />
          </div>
        )}
        <Button type="button" variant="danger" size="sm" onClick={() => setDeleteOpen(true)}>
          Delete provider permanently
        </Button>
      </section>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this provider permanently?"
        description={
          <>
            This permanently deletes <strong>{provider.provider_name}</strong> and cascades related
            data (assignments, known mappings, user links, ingestion history). Type the provider
            name to confirm.
          </>
        }
        confirmLabel="Delete permanently"
        confirmLoadingLabel="Deleting…"
        confirmLoading={deleting}
        tone="danger"
        confirmPhrase={provider.provider_name}
        onConfirm={handleHardDelete}
      />
    </>
  );
}
